import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { useQuery, useQueryClient } from 'react-query';
import { useAuthUser } from 'react-auth-kit';
import {
  activateAssetVersion,
  getAssetVersionLink,
  rejectAssetVersion,
  updateAssetVersionData,
  updateAssetVersionNeededBy,
} from 'src/api/functions/assetsApi';
import { getGroupMembers } from 'src/api/functions/userApi';
import { useAssetVersionsQuery } from 'src/api/queries/assetsQueries';
import { queryNames } from 'src/api/utils/queryNames';
import { useAlerts } from 'src/common/AlertManager';
import { Button } from 'src/components';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { Asset, AssetVersion, AssetVersionsResponse, ReleaseRole } from 'src/types/TAssets';
import { formatBytes } from 'src/utils/common';
import { getThumbnailUrl, thumbnailVersion } from 'src/utils/previews';
import MakeLiveDialog from './MakeLiveDialog';
import styles from './VersionsTab.module.scss';

const STATE_LABELS: Record<AssetVersion['state'], string> = {
  live: 'Live',
  pending: 'Waiting for release',
  superseded: 'Previous',
  rejected: 'Rejected',
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '');

// A needed-by date entered as a date is stored as midnight UTC: show it as
// that calendar day wherever the viewer is, and a time only when one was given.
const formatNeededBy = (value: string) =>
  /T00:00:00(\.0+)?Z$/.test(value)
    ? new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' })
    : new Date(value).toLocaleString();

const ROLE_NAMES: Record<ReleaseRole, string> = {
  ADMIN: 'Admins',
  MANAGER: 'Managers',
  CREATOR: 'Creators',
  USER: 'Users',
};

/** "Admins or Managers" */
const rolesPhrase = (roles?: ReleaseRole[]) => {
  const names = (roles?.length ? roles : (['ADMIN', 'MANAGER'] as ReleaseRole[])).map((r) => ROLE_NAMES[r] ?? r);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0];
};

const isOk = (response: any) => !(typeof response?.status === 'number' && response.status >= 400);

/** Private thumbnail of a non-live version: a short-lived presigned URL, fetched with the session token. */
const VersionThumbnail = ({ assetId, version }: { assetId: string; version: AssetVersion }) => {
  const accessToken = useAccessToken();
  const { data } = useQuery(
    [queryNames.assetVersions, assetId, version.version_no, 'thumbnail'],
    () => getAssetVersionLink(assetId, version.version_no, 'thumbnail', accessToken),
    { enabled: !!version.thumbnail_available, staleTime: 1000 * 60 * 3 },
  );
  const url = (data as any)?.data?.url;
  if (!url) {
    return <div className={styles.thumbPlaceholder}>{version.content_type?.split('/')[1] ?? 'file'}</div>;
  }
  return <img className={styles.thumb} src={url} alt={`Version ${version.version_no}`} />;
};

type VersionsTabProps = {
  asset: Asset;
};

export const VersionsTab: React.FC<VersionsTabProps> = ({ asset }) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showAlert, showSuccess } = useAlerts();
  const auth = useAuthUser();
  const { data: response, isLoading } = useAssetVersionsQuery(asset.id);
  const payload: AssetVersionsResponse | undefined = response?.data;

  // names for "submitted by"; the redacted group directory, same as the Notify modal
  const { data: membersResponse } = useQuery(
    [queryNames.groupMembers, '__versions__'],
    () => getGroupMembers(accessToken, undefined, 200),
    { staleTime: 1000 * 60 * 5 },
  );
  const memberNames = useMemo(() => {
    const names: Record<number, string> = {};
    ((membersResponse as any)?.data?.members ?? []).forEach((m: any) => {
      names[m.id] = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    });
    return names;
  }, [membersResponse]);
  const who = (userId?: number | null) => (userId ? memberNames[userId] ?? `User ${userId}` : '—');

  const [confirming, setConfirming] = useState<AssetVersion | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ label: string; note: string }>({ label: '', note: '' });
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [schedulingNeed, setSchedulingNeed] = useState<number | null>(null);
  const [needDraft, setNeedDraft] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries([queryNames.assetVersions, asset.id]);
    queryClient.invalidateQueries([queryNames.asset, asset.id]);
    queryClient.invalidateQueries([queryNames.search]);
  };

  if (isLoading || !payload) {
    return (
      <div className={styles.empty}>
        <p>Loading versions…</p>
      </div>
    );
  }

  const live = payload.versions.find((v) => v.state === 'live');
  const pending = payload.versions.filter((v) => v.state === 'pending');
  const previous = payload.versions.filter((v) => v.state === 'superseded');
  const rejected = payload.versions.filter((v) => v.state === 'rejected');
  const ordered = [...(live ? [live] : []), ...pending, ...previous, ...rejected];

  // Previous versions beyond the limit are deleted after the next one goes
  // live: the oldest goes first. Shown in the confirmation when it applies.
  const droppedBy = (target: AssetVersion): number | null => {
    const kept = previous.filter((v) => v.version_no !== target.version_no);
    if (kept.length + 1 <= payload.max_previous_versions) return null;
    const oldest = [...kept].sort((a, b) => a.version_no - b.version_no)[0];
    return oldest ? oldest.version_no : null;
  };

  const makeLive = async (version: AssetVersion) => {
    setBusy(version.version_no);
    try {
      const result = await activateAssetVersion(asset.id, version.version_no, accessToken);
      if (!isOk(result)) throw result;
      const liveNo = (result as any)?.data?.version ?? version.version_no;
      showSuccess(`Version ${liveNo} is now live. Formats are being regenerated.`);
      setConfirming(null);
      refresh();
      // the thumbnail and formats are regenerated in the background; refetch
      // so the preview catches up without a reload
      [5000, 15000, 40000].forEach((delay) => setTimeout(refresh, delay));
    } catch (e: any) {
      showAlert(e?.data?.message || e?.message || 'Could not make this version live.');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (version: AssetVersion) => {
    setBusy(version.version_no);
    try {
      const result = await rejectAssetVersion(asset.id, version.version_no, accessToken, rejectNote.trim() || undefined);
      if (!isOk(result)) throw result;
      showSuccess(`Version ${version.version_no} rejected.`);
      setRejecting(null);
      setRejectNote('');
      refresh();
    } catch (e: any) {
      showAlert(e?.data?.message || e?.message || 'Could not reject this version.');
    } finally {
      setBusy(null);
    }
  };

  const saveNeededBy = async (version: AssetVersion, value: string | null) => {
    setBusy(version.version_no);
    try {
      const result = await updateAssetVersionNeededBy(asset.id, version.version_no, value, accessToken);
      if (!isOk(result)) throw result;
      setSchedulingNeed(null);
      refresh();
    } catch (e: any) {
      showAlert(e?.data?.message || e?.message || 'Could not save the date.');
    } finally {
      setBusy(null);
    }
  };

  const download = async (version: AssetVersion) => {
    const result = await getAssetVersionLink(asset.id, version.version_no, 'download', accessToken);
    const url = (result as any)?.data?.url;
    if (url) window.open(url);
    else showAlert('Could not get a download link for this version.');
  };

  const saveLabel = async (version: AssetVersion) => {
    setBusy(version.version_no);
    try {
      const { label: _label, note: _note, ...rest } = version.version_data || {};
      const data: { [key: string]: any } = { ...rest };
      if (draft.label.trim()) data.label = draft.label.trim();
      if (draft.note.trim()) data.note = draft.note.trim();
      const result = await updateAssetVersionData(asset.id, version.version_no, data, accessToken);
      if (!isOk(result)) throw result;
      setEditing(null);
      refresh();
    } catch (e: any) {
      showAlert(e?.data?.message || e?.message || 'Could not save the label.');
    } finally {
      setBusy(null);
    }
  };

  const releasers = rolesPhrase(payload.settings.release_roles);
  const canEdit = payload.can_edit ?? payload.can_activate;
  const myId = Number(auth()?.userId) || null;
  const settingsNote = payload.settings.require_approval
    ? `Replacements wait here until one of the ${releasers} who can edit this asset makes them live.`
    : payload.settings.keep_history
      ? 'Replacing this asset keeps the current file as a previous version.'
      : 'Version history is off for your account: replacing this asset overwrites it.';

  return (
    <div className={styles.container} data-test="versions-tab">
      <p className={styles.summary}>
        {settingsNote} Up to {payload.max_previous_versions} previous version
        {payload.max_previous_versions === 1 ? ' is' : 's are'} kept.
      </p>

      {ordered.map((version) => {
        const isLive = version.state === 'live';
        const label = version.version_data?.label;
        const note = version.version_data?.note;
        const uploading = version.state === 'pending' && version.processing_status === 'uploading';
        const failed = version.processing_status === 'error';
        const canAct = payload.can_activate && busy === null;
        return (
          <div
            key={version.version_no}
            className={cn(styles.card, styles[version.state])}
            data-test={`version-${version.version_no}`}
          >
            <div className={styles.thumbBox}>
              {isLive ? (
                <img
                  className={styles.thumb}
                  alt={`Version ${version.version_no}`}
                  src={getThumbnailUrl({
                    width: 160,
                    height: 160,
                    id: asset.id,
                    jwt: accessToken,
                    version: thumbnailVersion(asset),
                  })}
                />
              ) : (
                <VersionThumbnail assetId={asset.id} version={version} />
              )}
            </div>
            <div className={styles.body}>
              <div className={styles.titleRow}>
                <span className={styles.versionNo}>v{version.version_no}</span>
                <span className={cn(styles.state, styles[`state_${version.state}`])}>
                  {STATE_LABELS[version.state]}
                </span>
                {label && <span className={styles.label}>{label}</span>}
              </div>
              <div className={styles.meta} title={version.name}>
                {version.name} · {formatBytes(version.file_size || 0, 1)}
                {version.width && version.height ? ` · ${version.width}×${version.height}` : ''}
              </div>
              <div className={styles.meta}>
                {isLive ? 'Live since' : version.state === 'superseded' ? 'Replaced' : 'Submitted'}{' '}
                {formatDate(isLive ? version.activated_at : version.superseded_at || version.submitted_at)}
                {!isLive && version.state !== 'superseded' ? ` by ${who(version.submitted_by)}` : ''}
              </div>
              {version.state === 'rejected' && (
                <div className={styles.meta}>
                  Rejected by {who(version.reviewed_by)} {formatDate(version.reviewed_at)}
                  {version.review_note ? ` — “${version.review_note}”` : ''}
                </div>
              )}
              {version.state === 'pending' && version.needed_by && schedulingNeed !== version.version_no && (
                <div className={cn(styles.meta, styles.neededBy)} data-test={`version-needed-by-${version.version_no}`}>
                  Needed by {formatNeededBy(version.needed_by)}
                </div>
              )}
              {version.has_pending_metadata && (
                <div className={styles.hint}>Metadata sent with this upload is applied when it goes live.</div>
              )}
              {uploading && <div className={styles.hint}>Processing the upload…</div>}
              {failed && <div className={styles.error}>This file could not be processed and cannot go live.</div>}
              {note && editing !== version.version_no && <div className={styles.note}>{note}</div>}

              {schedulingNeed === version.version_no ? (
                <div className={styles.editor} data-test={`version-needed-by-form-${version.version_no}`}>
                  {/* not inside .meta: that line is single-row with overflow hidden and clips the picker */}
                  <label className={styles.fieldLabel} htmlFor={`version-needed-by-${version.version_no}`}>
                    Needed live by (shown to approvers)
                  </label>
                  <input
                    id={`version-needed-by-${version.version_no}`}
                    type="date"
                    className={styles.input}
                    value={needDraft}
                    onChange={(e) => setNeedDraft(e.target.value)}
                    data-test={`version-needed-by-input-${version.version_no}`}
                  />
                  <div className={styles.actions}>
                    <Button mode="flat" size="small" onClick={() => setSchedulingNeed(null)} disabled={busy !== null}>
                      Cancel
                    </Button>
                    {version.needed_by && (
                      <Button mode="flat" size="small" onClick={() => saveNeededBy(version, null)} disabled={busy !== null}>
                        Clear date
                      </Button>
                    )}
                    <Button
                      mode="secondary"
                      size="small"
                      onClick={() => saveNeededBy(version, needDraft)}
                      disabled={busy !== null || !needDraft}
                      datatest={`version-needed-by-save-${version.version_no}`}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : rejecting === version.version_no ? (
                <div className={styles.editor} data-test={`version-reject-form-${version.version_no}`}>
                  <input
                    className={styles.input}
                    placeholder="Reason (optional), e.g. wrong crop"
                    value={rejectNote}
                    maxLength={500}
                    onChange={(e) => setRejectNote(e.target.value)}
                  />
                  <div className={styles.actions}>
                    <Button mode="flat" size="small" onClick={() => setRejecting(null)} disabled={busy !== null}>
                      Cancel
                    </Button>
                    <Button
                      mode="decline"
                      size="small"
                      onClick={() => reject(version)}
                      disabled={busy !== null}
                      datatest={`version-reject-confirm-${version.version_no}`}
                    >
                      Reject version {version.version_no}
                    </Button>
                  </div>
                </div>
              ) : editing === version.version_no ? (
                <div className={styles.editor}>
                  <input
                    className={styles.input}
                    placeholder="Label, e.g. Spring campaign"
                    value={draft.label}
                    maxLength={80}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  />
                  <textarea
                    className={styles.input}
                    placeholder="Note"
                    rows={2}
                    value={draft.note}
                    maxLength={500}
                    onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                  />
                  <div className={styles.actions}>
                    <Button mode="flat" size="small" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button mode="secondary" size="small" onClick={() => saveLabel(version)} disabled={busy !== null}>
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.actions}>
                  {canEdit && version.state !== 'rejected' && (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        setDraft({ label: label ?? '', note: note ?? '' });
                        setEditing(version.version_no);
                      }}
                    >
                      {label || note ? 'Edit label' : 'Add label'}
                    </button>
                  )}
                  {version.state === 'pending' && (payload.can_activate || version.submitted_by === myId) && (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        setNeedDraft(version.needed_by ? version.needed_by.slice(0, 10) : '');
                        setSchedulingNeed(version.version_no);
                      }}
                      data-test={`version-needed-by-edit-${version.version_no}`}
                    >
                      {version.needed_by ? 'Change needed-by date' : 'Set needed-by date'}
                    </button>
                  )}
                  {!isLive && version.state !== 'rejected' && !uploading && (
                    <button type="button" className={styles.linkButton} onClick={() => download(version)}>
                      Download
                    </button>
                  )}
                  {version.state === 'pending' && canAct && !uploading && (
                    <Button
                      mode="flat"
                      size="small"
                      onClick={() => {
                        setRejectNote('');
                        setRejecting(version.version_no);
                      }}
                      datatest={`version-reject-${version.version_no}`}
                    >
                      Reject
                    </Button>
                  )}
                  {(version.state === 'pending' || version.state === 'superseded') && canAct && !uploading && !failed && (
                    <Button
                      mode={version.state === 'pending' ? 'primary' : 'secondary'}
                      size="small"
                      onClick={() => setConfirming(version)}
                      datatest={`version-make-live-${version.version_no}`}
                    >
                      {version.state === 'pending' ? 'Make live' : 'Restore'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!payload.can_activate && (pending.length > 0 || previous.length > 0) && (
        <p className={styles.hint}>
          {payload.settings.require_approval
            ? `Only ${releasers} who can edit this asset can make a version live or reject it.`
            : 'Only people who can edit this asset can make a version live.'}
        </p>
      )}

      {confirming && live && (
        <MakeLiveDialog
          assetName={asset.name}
          liveVersion={live.version_no}
          version={confirming.version_no}
          isRestore={confirming.state === 'superseded'}
          dropsOldest={droppedBy(confirming)}
          busy={busy !== null}
          onConfirm={() => makeLive(confirming)}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
};

export default VersionsTab;
